import { useEffect, useMemo, useState } from "react";
import { Search, UserPlus } from "lucide-react";

import { useAssets, useCheckOut, useCreatePerson, usePeople } from "@/api/queries";
import type { Asset, Person } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { useToast } from "@/components/toast-context";
import { reportMutationError } from "@/lib/mutation-errors";
import { formatPersonType } from "@/lib/format";

/**
 * Lend one tool to one person (Frontend Spec §17).
 *
 * The asset list is filtered to what is actually lendable — `available_only`
 * excludes both the broken and the already-out, server-side. Offering a drill
 * that Patrick has had for a week and refusing it on submit would be a worse
 * screen than not offering it at all.
 */
export function CheckOutDialog({
  open,
  onOpenChange,
  assetId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected when the dialog is opened from an asset. */
  assetId?: string;
}) {
  const toast = useToast();
  const checkOut = useCheckOut();
  const createPerson = useCreatePerson();

  const [assetTerm, setAssetTerm] = useState("");
  const [debouncedAsset, setDebouncedAsset] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<string>(assetId ?? "");
  const [personId, setPersonId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [notes, setNotes] = useState("");
  const [newPersonName, setNewPersonName] = useState("");
  const [addingPerson, setAddingPerson] = useState(false);

  useEffect(() => {
    if (open) setSelectedAsset(assetId ?? "");
  }, [open, assetId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAsset(assetTerm.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [assetTerm]);

  const { data: assets } = useAssets(
    { q: debouncedAsset || undefined, available_only: true, limit: 20 },
    { enabled: open && !assetId },
  );
  const { data: people } = usePeople({ limit: 200 }, { enabled: open });

  const chosen: Asset | undefined = useMemo(
    () => assets?.items.find((item) => item.id === selectedAsset),
    [assets, selectedAsset],
  );

  function reset() {
    setAssetTerm("");
    setSelectedAsset(assetId ?? "");
    setPersonId("");
    setDueOn("");
    setNotes("");
    setNewPersonName("");
    setAddingPerson(false);
  }

  async function addPerson() {
    const name = newPersonName.trim();
    if (!name) return;
    try {
      const person = await createPerson.mutateAsync({ display_name: name });
      setPersonId(person.id);
      setNewPersonName("");
      setAddingPerson(false);
    } catch (error) {
      toast.error("Personne non créée", String(error));
    }
  }

  async function submit() {
    if (!selectedAsset || !personId) return;
    try {
      const loan = await checkOut.mutateAsync({
        asset_id: selectedAsset,
        person_id: personId,
        due_on: dueOn || null,
        notes: notes || null,
        // The field workflow is a phone in a truck: a double tap must not lend
        // the same drill twice.
        idempotency_key: `loan-${selectedAsset}-${personId}-${dueOn || "open"}`,
      });
      toast.success("Outil prêté", `${loan.asset_code} → ${loan.person_name}`);
      reset();
      onOpenChange(false);
    } catch (error) {
      reportMutationError(toast, "Prêt refusé", error);
    }
  }

  const ready = Boolean(selectedAsset && personId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouveau prêt"
      description="Un outil, une personne, et la date à laquelle vous le voulez de retour."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!ready || checkOut.isPending}>
            {checkOut.isPending ? "Enregistrement…" : "Prêter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!assetId && (
          <Field label="Outil">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
                aria-hidden
              />
              <Input
                value={assetTerm}
                onChange={(event) => setAssetTerm(event.target.value)}
                placeholder="Code, numéro de série, nom…"
                className="pl-9"
                aria-label="Chercher un outil disponible"
              />
            </div>
            <Select
              value={selectedAsset}
              onChange={(event) => setSelectedAsset(event.target.value)}
              aria-label="Outil à prêter"
              className="mt-2"
            >
              <option value="">Choisir un outil…</option>
              {assets?.items.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.asset_code} — {asset.name ?? asset.product_name}
                  {asset.serial_number ? ` (${asset.serial_number})` : ""}
                </option>
              ))}
            </Select>
            {assets && assets.items.length === 0 && (
              <p className="mt-1 text-[12px] text-ink-subtle">
                Aucun outil disponible ne correspond. Les outils déjà sortis ou hors service ne
                sont pas proposés.
              </p>
            )}
          </Field>
        )}

        {chosen && (
          <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-[12px] text-ink-muted">
            État actuel : {chosen.condition}. Il sera enregistré comme état de sortie.
          </p>
        )}

        <Field label="Personne">
          {addingPerson ? (
            <div className="flex gap-2">
              <Input
                value={newPersonName}
                onChange={(event) => setNewPersonName(event.target.value)}
                placeholder="Nom de la personne"
                aria-label="Nom de la nouvelle personne"
              />
              <Button
                variant="secondary"
                onClick={() => void addPerson()}
                disabled={!newPersonName.trim() || createPerson.isPending}
              >
                Ajouter
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Select
                value={personId}
                onChange={(event) => setPersonId(event.target.value)}
                aria-label="Emprunteur"
              >
                <option value="">Choisir une personne…</option>
                {people?.items.map((person: Person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name} — {formatPersonType(person.person_type)}
                  </option>
                ))}
              </Select>
              <Button variant="ghost" onClick={() => setAddingPerson(true)} aria-label="Nouvelle personne">
                <UserPlus className="size-4" aria-hidden />
              </Button>
            </div>
          )}
          {people && people.items.length === 0 && !addingPerson && (
            <p className="mt-1 text-[12px] text-ink-subtle">
              Aucune personne enregistrée. Ajoutez-en une avec le bouton à droite.
            </p>
          )}
        </Field>

        <Field
          label="Retour prévu"
          hint="Facultatif. Sans date, le prêt n'apparaîtra jamais en retard."
        >
          <Input
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
            aria-label="Date de retour prévue"
          />
        </Field>

        <Field label="Note" hint="Facultatif.">
          <Input
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Chantier, accessoires partis avec l'outil…"
          />
        </Field>
      </div>
    </Dialog>
  );
}
